import classNames from 'classnames';
import { isNumber } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Scrollbars, SliderInput } from '@meitu/saas-ui';
import { CustomTitle } from '@meitu/saas-ui/es/title';
import { getTextMarkConfig, type TextMarkStruct } from '@meitu/saas-editor-core/es/layers/text-layer';
import { textMarkDefaultSize } from '@meitu/saas-editor-core/es/layers/text-layer/config';
import { imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import {
    type ILayerProps,
    type ITextContent,
    type ITextLayerProps,
    type ITextMark,
} from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { ColorGroup } from '@meitu/xdesign-widgets';
import LabelAction from '@/components/label-action';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { checkIsInArtTextGeneratorMode } from '@/utils/mode';
import iconPRO from '../../assets/icon-pro.png';
import iconVIP from '../../assets/icon-svip.png';
import DragPopover from '../../components/drag-popover';
import { TextPanelContext } from '../../context';
import { useEditingRange } from '../../hooks';
import { getTextMarkThumbnailURL, Tracker } from '../../utils';
import './style.less';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';

const COLOR_PICKER_DOM_CLASS = 'style-paint-color-picker';

// FIXME: 划重点数据加载方式非常怪异，需要调整

type Props = {
    onClose: () => void;
};

export const TEXT_MARK_POPOVER = 'text-mark-popover';

export const TextMarkPanel: React.FC<Props> = ({ onClose }) => {
    const { layerId, recordId, isIntl, theme } = useContext(TextPanelContext)!;

    const { $t } = useLocaleStore();

    // 插入操作组件的索引
    const [insertIndex, setInsertIndex] = useState<number>(-1);

    // 添加一个 key 来控制组件重新创建
    const [actionKey, setActionKey] = useState(0);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scrollbarsRef = useRef<any>(null);
    const draggleRef = useRef<HTMLDivElement>(null);
    const editor = useEditor();
    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        return {
            textMark: layerData.textMark,
            textContents: layerData.textContents,
            advancedTextMaterial: layerData.advancedTextMaterial,
        };
    });
    const editingRange = useEditingRange(editor);
    const popoverWidgetRef = useGlobalWidget(editor, false);

    const textMarkMaterialsRef = useRef<TextMarkStruct[]>(getTextMarkConfig());
    const originLayer = useRef<ILayerProps | null>(null);

    const textMark = props?.textMark || null;
    const textContents = props?.textContents || ([] as ITextContent[]);
    const advancedTextMaterial = props?.advancedTextMaterial;

    const ignoreRange = !checkIsInArtTextGeneratorMode() && !!advancedTextMaterial;

    const targetTextMark = (() => {
        // 1. 若没有选区，或者选区是折叠的：
        // 1.1 如果整体有 textMark，返回整体的 textMark
        // 1.2 如果整体没有 textMark，则确认分段中是否所有 textMark 都一致，若一致则返回一致的 textMark，否则返回 null
        if (!editingRange || editingRange.isCollapsed) {
            if (textMark) return textMark;
            if (textContents.length > 0) {
                const textMarks = textContents.map((item) => item.textMark).filter(Boolean);
                const uniqueTextMarks = [...new Set(textMarks.map((item) => JSON.stringify(item)))];

                if (uniqueTextMarks.length === 1) return textMarks[0];
                return null;
            }
            return null;
        }

        const { startIndex, endIndex } = editingRange;

        // 2. 若选区在的单个分段内，则返回该分段的 textMark，若该分段没有 textMark，则返回文本图层整体的 textMark
        if (startIndex === endIndex) {
            const textContent = textContents[startIndex];
            return textContent ? textContent.textMark : textMark ? textMark : null;
        }

        // 3. 若选区跨多个分段，则需要判断选区的开始到结束分段的 textMark 是否完全一致
        const editingTextContents = textContents.slice(startIndex, endIndex + 1);
        if (editingTextContents.length === 0 || editingTextContents.length !== endIndex - startIndex + 1) return null;

        const firstTextMark = editingTextContents[0].textMark;
        if (!firstTextMark) return null;

        for (let i = 1; i < editingTextContents.length; i++) {
            const textMark = editingTextContents[i].textMark;
            if (!textMark || JSON.stringify(textMark) !== JSON.stringify(firstTextMark)) return null;
        }

        return firstTextMark;
    })();

    const targetTextMarkConfig = targetTextMark
        ? textMarkMaterialsRef.current.find((item) => String(item.materialId) === String(targetTextMark.materialId))
        : null;

    const enableOpacity = targetTextMarkConfig?.data.enableOpacity ?? true;

    const renderCornerIcon = () => {
        if (isIntl) {
            return <img className="text-mark-panel__icon" src={iconPRO} />;
        }
        return <img className="text-mark-panel__icon" src={iconVIP} />;
    };

    const setTextMark = (item: TextMarkStruct) => {
        if (textMark && item.materialId === textMark.materialId) return;

        // 先重置 actionKey 触发重新渲染（解决：如果打开了颜色面板，关不掉）
        setActionKey((prev) => prev + 1);

        const data: ITextMark = {
            materialId: String(item.materialId),
            enableSize: item.data.enableSize,
            size: textMarkDefaultSize,
            color: item.data.defaultColor,
        };
        editor.getInstance(TextLayerExtension).setTextMark(layerId, data, { ignoreRange });

        // eslint-disable-next-line react-hooks/rules-of-hooks
        Tracker.useTextMark({
            recordId,
            stageType: editor.stage.getType(),
        });
    };

    const clearTextMark = () => {
        editor.getInstance(TextLayerExtension).cancelTextMark(layerId, { ignoreRange });
    };

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.stage.getType(),
                kind: 'textMark',
            });
        }
    };

    const handleColorChange = (data: ITextMark, value: string) => {
        if (originLayer.current == null) {
            originLayer.current = editor.stage.getLayer(layerId, true);
        }

        let color = value;

        if (!enableOpacity) color = value.slice(0, 7);

        const textMark: ITextMark = { ...data, color };
        editor.getInstance(TextLayerExtension).updateTextMark(layerId, textMark, { ignoreRange });
    };

    const handleColorAfterChange = (data: ITextMark, value: string) => {
        let color = value;

        if (!enableOpacity) color = value.slice(0, 7);

        const textMark: ITextMark = { ...data, color };
        editor.getInstance(TextLayerExtension).updateTextMark(layerId, textMark, {
            originLayer: originLayer.current ? originLayer.current : undefined,
            ignoreRange,
        });

        originLayer.current = null;

        Tracker.adjustTextMarkProps({
            recordId,
            stageType: editor.stage.getType(),
            isColor: true,
        });
    };

    const handleSizeChange = (data: ITextMark, value: number) => {
        if (!data.enableSize || !isNumber(value)) return;

        if (originLayer.current == null) {
            originLayer.current = editor.stage.getLayer(layerId, true);
        }

        const textMark: ITextMark = { ...data, size: value };
        editor.getInstance(TextLayerExtension).updateTextMark(layerId, textMark, { ignoreRange });
    };

    const handleSizeAfterChange = (data: ITextMark, value: number) => {
        if (!data.enableSize || !isNumber(value)) return;

        const textMark: ITextMark = { ...data, size: value };
        editor.getInstance(TextLayerExtension).updateTextMark(layerId, textMark, {
            originLayer: originLayer.current ? originLayer.current : undefined,
            ignoreRange,
        });

        originLayer.current = null;

        Tracker.adjustTextMarkProps({
            recordId,
            stageType: editor.stage.getType(),
            isColor: false,
        });
    };

    //选择后，在对应行后面插入操作组件
    useEffect(() => {
        if (!targetTextMark) return;
        const index = textMarkMaterialsRef.current.findIndex(
            (item) => String(item.materialId) === String(targetTextMark.materialId),
        );
        if (index === -1) return;
        const activeIndex = index + 1; //+1 是因为第一个元素是自定义的重置元素
        const insertIndex = Math.floor(activeIndex / 3) * 3 + 2;
        const lastElIndex = textMarkMaterialsRef.current.length;
        setInsertIndex(insertIndex >= lastElIndex ? lastElIndex : insertIndex);

        const activeEl = document.querySelector(`.text-mark-panel__item:nth-of-type(${activeIndex})`) as HTMLElement;
        if (activeEl) requestAnimationFrame(() => scrollbarsRef.current?.scrollTop(activeEl.offsetTop - 16)); // 减去16px，是因为操作组件的padding-top为16px
    }, [targetTextMark?.materialId]);

    //  渲染操作组件
    const renderActions = () => {
        return targetTextMark && targetTextMarkConfig ? (
            <div className="text-mark-panel__actions">
                {/* 颜色 */}
                <LabelAction title={$t((v) => v.textMarkSelector.color)} align>
                    <ColorGroup
                        popupTriggerSize={{ width: 58, height: 26 }}
                        color={targetTextMark.color}
                        hideAlpha={!enableOpacity}
                        isGradient={false}
                        setColor={(color) => handleColorChange(targetTextMark, color)}
                        setColorAfter={(color, _, { isBrandColor }) => {
                            handleColorAfterChange(targetTextMark, color);
                            if (isBrandColor) {
                                window._stat_track?.('hb_layers_func_use', {
                                    func_id: '1_107',
                                });
                            }
                        }}
                        showIcon={false}
                        popupOverlayClassName={COLOR_PICKER_DOM_CLASS}
                        placement="topLeft"
                        onOpenChange={handleOpenChange}
                        onToggleBizTab={(bizTab) => {
                            if (bizTab === 'team') {
                                window._stat_track?.('hb_layers_func_click', {
                                    func_id: '1_107',
                                });
                            }
                        }}
                    />
                </LabelAction>

                {/* 粗细 */}
                {targetTextMark.enableSize ? (
                    <LabelAction title={$t((v) => v.textMarkSelector.size)} align>
                        <div style={{ flex: 1 }}>
                            <SliderInput
                                min={1}
                                max={100}
                                value={targetTextMark.size}
                                precision={1}
                                showStepBtn
                                type="horizontal"
                                onChange={(value) => handleSizeChange(targetTextMark, value)}
                                onAfterChange={(value) => handleSizeAfterChange(targetTextMark, value)}
                            />
                        </div>
                    </LabelAction>
                ) : null}
            </div>
        ) : null;
    };

    return (
        <DragPopover draggleRef={draggleRef} innerDoms={[COLOR_PICKER_DOM_CLASS]}>
            <div className="text-mark-panel" ref={draggleRef}>
                <div className="shape-text-panel__title">
                    <CustomTitle
                        leftNode={<div className="left">{$t((v) => v.panel.textPanel.textMark)}</div>}
                        rightNode={
                            <div className="right" onClick={onClose}>
                                <IconFont name="CrossBlack" fontSize={12} color="var(--content-system-secondary)" />
                            </div>
                        }
                    />
                </div>

                {/* safari低版本需要包一层div 滚动列表才能撑开*/}
                <div style={{ flex: 1 }}>
                    <Scrollbars autoHide scrollbarsRef={scrollbarsRef}>
                        <div className="text-mark-panel__list">
                            <div className="text-mark-panel__materials">
                                <div className="text-mark-panel__item" onClick={clearTextMark}>
                                    <IconFont name="SlashCircleBold" fontSize={28} />
                                </div>

                                {textMarkMaterialsRef.current.map((item, index) => {
                                    const active =
                                        targetTextMark && String(item.materialId) === String(targetTextMark.materialId);
                                    const cls = classNames('text-mark-panel__item', {
                                        'text-mark-panel__item--active': active,
                                    });
                                    const url = getTextMarkThumbnailURL(item, theme, isIntl);
                                    const cover = url ? imageConverter(url, 140) : '';

                                    return (
                                        <React.Fragment key={item.materialId}>
                                            <div
                                                className={cls}
                                                key={item.materialId}
                                                style={{ backgroundImage: `url(${cover})` }}
                                                onClick={() => setTextMark(item)}
                                            >
                                                {item.isVip ? renderCornerIcon() : null}
                                            </div>
                                            {/* 插入操作组件 */}
                                            {insertIndex - 1 === index && <p key={actionKey}>{renderActions()}</p>}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>
                    </Scrollbars>
                </div>
            </div>
        </DragPopover>
    );
};
